// src/shared/components/ui/dropdown-menu.tsx

import { useEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/shared/utils/cn'

interface DropdownMenuProps {
  trigger: ReactNode
  align?: 'left' | 'right'
  className?: string
  children: (close: () => void) => ReactNode
}

export function DropdownMenu({ trigger, align = 'right', className, children }: DropdownMenuProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  return (
    <div className={cn('relative inline-flex', className)} ref={wrapRef}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        {trigger}
      </button>
      {open ? (
        <div
          className={cn(
            'absolute top-full z-30 mt-1 min-w-[180px] rounded-md border border-[var(--panel-border)] bg-[var(--panel)] p-1 shadow-md',
            align === 'right' ? 'right-0' : 'left-0',
          )}
          role="menu"
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  )
}

interface DropdownItemProps {
  onClick?: () => void
  disabled?: boolean
  destructive?: boolean
  children: ReactNode
  icon?: ReactNode
}

export function DropdownItem({
  onClick,
  disabled,
  destructive,
  icon,
  children,
}: DropdownItemProps) {
  return (
    <button
      className={cn(
        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition',
        disabled && 'cursor-not-allowed opacity-50',
        !disabled && !destructive && 'text-[var(--color-text)] hover:bg-[var(--panel-subtle)]',
        !disabled &&
          destructive &&
          'text-[var(--color-danger-fg)] hover:bg-[var(--color-danger-bg)]',
      )}
      disabled={disabled}
      onClick={onClick}
      role="menuitem"
      type="button"
    >
      {icon ? <span className="flex h-4 w-4 items-center justify-center">{icon}</span> : null}
      <span className="flex-1">{children}</span>
    </button>
  )
}
